import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useCookies } from "react-cookie";
import { ErrorBoundary } from "react-error-boundary";
import axios from "axios";
import DisableDevtool from "disable-devtool";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "react-query";

import Router from "./router";
import { ErrorPage } from "custom-components-v1";
import { ThemeStore } from "./context/ThemeStore";

import "./styles/common.scss";
import { ThemeProvider } from "styled-components";
import { useSelector } from "react-redux";
import { ConfigProvider, theme } from "antd";
import CacheInvalidator from "./components/global/CacheInvalidator";
import { setUser } from "./redux/slices/userSlice";
import { useDispatch } from "react-redux";

const queryClient = new QueryClient();
const UP = JSON.parse(localStorage.getItem("userProfile"));
const isOnboardingV2Done = UP ? UP.isOnboardingV2Done : false;
const isLms = sessionStorage.getItem("lms");
import "custom-components-v1/dist/index.css";
import { useNavigate, useSearchParams, matchPath, useLocation } from "react-router-dom";

const PATHS_TO_IGNORE = ["/onboarding", "/auth", "/maintenance", "/auth/login", "/auth/reset/request", "/logout"];

const useQuery = () => new URLSearchParams(useLocation().search);
const App = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [cookies, setCookies] = useCookies();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const setCookieHeader = async () => {
      if (searchParams.get("t")) {
        setCookies("token", searchParams.get("t"), {
          path: "/",
          httpOnly: false,
          secure: false,
        });
        axios.defaults.headers.common["Authorization"] = `Bearer ${searchParams.get("t")}`;
        window.localStorage.removeItem("userProfile");
      } else {
        axios.defaults.headers.common["Authorization"] = `Bearer ${cookies["token"]}`;
      }
      if (window.location.pathname.includes("/auth")) {
        return;
      }
      const utmSource = query.get("utm_source");
      const utmMedium = query.get("utm_medium");
      const utmCampaign = query.get("utm_campaign");
      try {
        const res = await axios.get("/api/auth/user");
        dispatch(setUser(res?.data?.data));
      } catch (error) {
        if (window.location.pathname !== "/auth/login") {
          const params = new URLSearchParams();

          if (utmSource) params.append("utm_source", utmSource);
          if (utmMedium) params.append("utm_medium", utmMedium);
          if (utmCampaign) params.append("utm_campaign", utmCampaign);

          const queryString = params.toString();
          navigate(`/auth/login${queryString ? `?${queryString}` : ""}`);
        }
      }
    };
    setCookieHeader();
  }, []);

  // useEffect(() => {
  //   window.location.href = "https://mathongo.com/ayjr";
  // }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      // Always validate the origin
      const allowedOrigins = [
        "http://localhost:3000",
        "http://localhost:3001",
        "https://ayjr-test.quizrr.in",
        "https://ayjr-test-staging.quizrr.in",
        "https://ayjr.quizrr.in",
        "https://ayjr-staging.quizrr.in",
      ];

      if (!allowedOrigins.includes(event.origin)) return;

      const { action, path } = event.data || {};

      // Handle reload and focus
      if (action === "reloadAndFocus") {
        window.focus();
        window.location.reload();
      }

      // Handle redirect
      if (action === "redirect" && path) {
        window.location.href = path;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const ALLOWED_ROUTES = [
    "/",
    "/v2/home",
    "/:packId/:testId/details",
    "/auth/login",
    "/v3/profile",
    "/tests/:pack/:test/submission",
  ];

  const { isDark } = useSelector((state) => state.user);
  useEffect(() => {
    // TODO: remove this on AYJR Live
    // window.location.href = "https://www.mathongo.com/ayjr";

    if (UP && !isOnboardingV2Done && PATHS_TO_IGNORE.indexOf(window.location.pathname) === -1 && !isLms) {
      navigate("/v2/home");
    }

    const currentPath = location.pathname;
    const isAllowedRoute = ALLOWED_ROUTES.some((pattern) => matchPath({ path: pattern, end: true }, currentPath));
    axios
      .get("/api/platform")
      .then((res) => {
        const data = res?.data?.data;
        if (!data?.isLoginEnabled) {
          window.location.href = data?.redirectUrl;
          return;
        }
        if (new Date(data?.detailAccessDate) > new Date()) {
          if (!isAllowedRoute) {
            navigate("/v2/home?showNotAccessibleModal=true");
          }
        }
      })
      .catch((err) => {
        window.location.href = "https://www.mathongo.com/ayjr";
      });
  }, [navigate, location.pathname]);

  return (
    <ErrorBoundary
      onError={(error, componentStack) => {
        console.log({ error, componentStack });
      }}
      fallbackRender={({ resetErrorBoundary }) => <ErrorPage errorCode={503} resetErrorBoundary={resetErrorBoundary} />}
    >
      <ThemeStore>
        <CacheInvalidator />
        <Helmet>
          <meta charset="UTF-8" />
          <meta http-equiv="X-UA-Compatible" content="IE=edge" />
          <meta name="robots" content="imageindex, follow, snippet, index" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>{`AYJR | MathonGo`}</title>
          <link rel="manifest" href="/manifest.json" />
          <link href="/icons/favicon-16x16.png" rel="icon" type="image/png" sizes="16x16" />
          <link href="/icons/favicon-32x32.png" rel="icon" type="image/png" sizes="32x32" />
          <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png"></link>
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png"></link>
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png"></link>
          <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#2e005b"></link>
          <meta name="theme-color" content="#317EFB" />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;800;900&display=swap"
            rel="stylesheet"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Caveat:wght@400..700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap"
            rel="stylesheet"
          />
        </Helmet>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider
            theme={{
              isDark: isDark,
            }}
          >
            <ConfigProvider
              theme={{
                algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
                token: isDark ? THEME.dark : THEME.light,
              }}
            >
              <Toaster />
              <Router />
            </ConfigProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </ThemeStore>
    </ErrorBoundary>
  );
};

const THEME = {
  light: {},
  dark: {
    colorBgContainer: "#152e4d",
    colorBgElevated: "#152e4d",
    colorText: "#fff",
  },
};

export default App;
